import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Button, Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { CategoryPickerCategory, CategoryPickerComponent } from "@me-pos/category-picker";
import i18next from "i18next";

import { EditMenuService } from "../service/editMenuService";
import { CategoryInterface } from "../../Products/types";
import {
    useGetCategoriesForAddDishModalQuery,
    useGetDishesForAddDishModalQuery,
} from "@me-team/host/main/editMenuPage/graphql/editMenuPage.hooks";
import { useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { debounce } from "@me-team/host/src/utils/utils";
import { DishesInterfaceForModal } from "../interfaces/interface";
import altDishImg from "../images/altDishImg.png";
import { Spinner, WithRoleAccess } from "@services/ui-components";
import { Link } from "react-router-dom";
import { EmptyDishListBySearch } from "./EmptyDishListBySearch";
import { EmptyDishList } from "./EmptyDishList";
import { menuLanguageVersionVar } from "@me-team/host/main/menuLanguageVersions/state";
import { useMenuLanguagesData } from "../../Me-menu/utils/hooks/useMenuLanguagesData";
import { buildTranslationTextVm } from "../../Me-menu/utils/translationVersions/buildTranslationTextVm";
import TranslationMissingDot from "../../Me-menu/components/LanguageVersionSelect/TranslationMissingDot";
import { cloneNameToDisplay } from "../../Me-menu/utils/cloneNameUtils";
import BrushIcon from "./icons/brushIcon.svg";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

type SelectedCategoryStateInterface = {
    categoryId: number | null;
    categoryName: string;
    categoryNameOrigin?: string | null;
    subcategoryId?: number | null;
    subcategoryName?: string | null;
};

type AddDishesModalProps = {
    branchId: number;
    menuId: number;
    isOpen: boolean;
    onClose: () => void;
    onAddDishesHandler: (dishesToAdd: Number[]) => void;
    selectedCategory: SelectedCategoryStateInterface;
    canEditMenuContent?: boolean;
    canEditDish?: boolean;
};

type FilterValuesType = {
    searchValue: string;
    category: { id: number | null; name: string | null };
    subcategory: { id: number | null; name: string | null };
};

const AddDishesModal: React.FC<AddDishesModalProps> = (props) => {
    const { t: translate } = useTranslation();
    const canEditMenuContentByRole = useRoleAccess(CompanyAccess.MenuCloneEdit);
    const canEditDishByRole = useRoleAccess(CompanyAccess.MenuDishEdit);
    const canEditMenuContent = props.canEditMenuContent ?? canEditMenuContentByRole;
    const canEditDish = props.canEditDish ?? canEditDishByRole;
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const selectedLangVersionCode = useReactiveVar(menuLanguageVersionVar);
    const { primaryLanguage } = useMenuLanguagesData(Number(currentCompanyId));
    const primaryLanguageIso = primaryLanguage?.isoCode?.trim() || null;
    const isTranslationMode = Boolean(
        selectedLangVersionCode && primaryLanguageIso && selectedLangVersionCode !== primaryLanguageIso
    );
    const translationTooltip = translate("Edit the language version");
    const basePath = process.env.REACT_APP_POS_URL;

    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [categoriesListForPicker, setCategoriesListForPicker] = useState<CategoryInterface[]>([]);
    const [dishesListForPicker, setDishesListForPicker] = useState<DishesInterfaceForModal[]>([]);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const [searchDraft, setSearchDraft] = useState("");
    const [filterValues, setFilterValues] = useState<FilterValuesType>({
        searchValue: "",
        category: { id: null, name: "" },
        subcategory: { id: null, name: "" },
    });

    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

    const componentRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const debouncedApplySearch = useMemo(
        () =>
            debounce((val: string) => {
                setFilterValues((prev) => ({ ...prev, searchValue: val }));
            }, 400),
        []
    );

    const { data: categoryData, loading: loadingCategoryData } = useGetCategoriesForAddDishModalQuery({
        skip: !currentCompanyId || !props.isOpen || !selectedLangVersionCode,
        context: { errorType: "global" },
        variables: { companyId: Number(currentCompanyId), locale: selectedLangVersionCode },
    });

    const pikedCategoryId = props.selectedCategory.subcategoryId
        ? props.selectedCategory.subcategoryId
        : props.selectedCategory.categoryId;

    const { data: dishesList, loading: loadingDishesList } = useGetDishesForAddDishModalQuery({
        skip: !currentCompanyId || !props.isOpen || !selectedLangVersionCode,
        context: { errorType: "global" },
        variables: {
            companyId: Number(currentCompanyId),
            menuId: Number(props.menuId),
            searchDishName: filterValues.searchValue,
            searchCategoryId: filterValues.subcategory.id ? filterValues.subcategory.id : filterValues.category.id,
            locale: selectedLangVersionCode,
            excludeForCategory: true,
            excludedCategoryId: pikedCategoryId,
        },
    });

    useEffect(() => {
        if (categoryData) {
            setCategoriesListForPicker(
                EditMenuService.initCategoriesInterface(categoryData.user.company.meMenuCategories, isTranslationMode)
            );
        }
    }, [categoryData, isTranslationMode]);

    useEffect(() => {
        const list = dishesList?.user?.company?.menuItemsList;
        if (list) {
            setDishesListForPicker(EditMenuService.initDishesInterface(list));
        } else if (dishesList) {
            setDishesListForPicker([]);
        }
    }, [dishesList]);

    useEffect(() => {
        if (!props.isOpen) {
            setIsPickerOpen(false);
            setSelectedIds(new Set());
            setSearchDraft("");
            setFilterValues({
                searchValue: "",
                category: { id: null, name: "" },
                subcategory: { id: null, name: "" },
            });
            setDishesListForPicker([]);
        }
    }, [props.isOpen]);

    const toggleDish = (dish: DishesInterfaceForModal) => {
        if (!dish.isAvailableForCurrentMenu) return;

        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(dish.id)) next.delete(dish.id);
            else next.add(dish.id);
            return next;
        });
    };

    const handleAdd = () => {
        if (!canEditMenuContent) return;
        const ids = Array.from(selectedIds);
        props.onAddDishesHandler(ids);
    };

    const loading = loadingDishesList || loadingCategoryData;
    const dishesReady = !!dishesList;

    const isAddDisabled = selectedIds.size === 0 || !canEditMenuContent;

    const isEmpty = dishesListForPicker.length === 0;
    const hasSearch = !!filterValues?.searchValue;
    const isSearchDisabled = isEmpty && !hasSearch;
    const selectedCategoryNameVm = buildTranslationTextVm({
        translatedValue: props.selectedCategory?.categoryName,
        originValue: props.selectedCategory?.categoryNameOrigin,
        isTranslationMode,
    });
    const isClearDisabled = !searchDraft && !filterValues.category.id && !filterValues.subcategory.id;

    const unavailableTooltip = translate("Unavailable for this menu type. Change availability in the dish settings.");

    if (!dishesListForPicker || !categoriesListForPicker) return null;

    return (
        <Modal
            show={props.isOpen}
            onHide={props.onClose}
            centered
            size="lg"
            dialogClassName="p-0"
            contentClassName="border-0"
        >
            <Modal.Header closeButton className="border-bottom-0 pb-0">
                <Modal.Title>
                    <span className="me-1">
                        {translate("Add dish to category")} “{selectedCategoryNameVm.displayText}”
                    </span>
                    {selectedCategoryNameVm.isMissingTranslation ? (
                        <TranslationMissingDot tooltipText={translationTooltip} />
                    ) : null}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="pt-3">
                <Row className="gx-3">
                    <Col>
                        <div className="position-relative">
                            <input
                                ref={searchInputRef}
                                type="text"
                                className="form-control pe-5"
                                placeholder={translate("Search")}
                                value={searchDraft}
                                onChange={(event) => {
                                    const normalized = EditMenuService.normalizeSearch(event.target.value);
                                    setSearchDraft(normalized);
                                    debouncedApplySearch(normalized);
                                }}
                                disabled={isSearchDisabled}
                            />

                            <button
                                type="button"
                                className="btn p-0 border-0 bg-transparent position-absolute top-50 end-0 translate-middle-y text-muted me-3"
                                aria-label={translate("Search")}
                                onClick={() => {
                                    if (!searchInputRef.current?.disabled) {
                                        searchInputRef.current.focus();
                                    }
                                }}
                                disabled={isSearchDisabled}
                            >
                                <i className="bi bi-search" />
                            </button>
                        </div>
                    </Col>

                    <Col>
                        <div className="position-relative" ref={componentRef}>
                            <input
                                type="text"
                                className="form-control pe-5 cursor-pointer"
                                placeholder={translate("Category/subcategory")}
                                ref={inputRef}
                                value={
                                    filterValues?.category.name || filterValues?.subcategory.name
                                        ? `${filterValues?.category.name ?? ""}${
                                              filterValues?.subcategory.name
                                                  ? ` / ${filterValues?.subcategory.name}`
                                                  : ""
                                          }`
                                        : translate("All Categories")
                                }
                                onClick={(e) => {
                                    e.preventDefault();
                                    setIsPickerOpen((prev) => !prev);
                                }}
                                disabled={isSearchDisabled}
                                readOnly
                            />

                            <i
                                className="bi bi-chevron-down position-absolute top-50 end-0 translate-middle-y text-muted me-3 cursor-pointer"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setIsPickerOpen((prev) => !prev);
                                    inputRef.current?.focus();
                                }}
                            />

                            {isPickerOpen && (
                                <div className="dropdown position-absolute z-2 start-0 end-0 mt-2 bg-white shadow">
                                    {categoryData && categoriesListForPicker && (
                                        <CategoryPickerComponent
                                            i18n={i18next}
                                            isOpen={isPickerOpen}
                                            showMissingTranslationDot={isTranslationMode}
                                            renderMissingTranslationDot={() => (
                                                <TranslationMissingDot tooltipText={translationTooltip} />
                                            )}
                                            onSearchChange={() => {}}
                                            onChangeCategory={(currentCategory: CategoryPickerCategory) => {
                                                if (currentCategory.isPicked) {
                                                    setFilterValues((prev) => ({
                                                        ...prev,
                                                        category: {
                                                            id: currentCategory.id,
                                                            name: currentCategory.name,
                                                        },
                                                        subcategory: { id: null, name: "" },
                                                    }));
                                                    setIsPickerOpen(false);
                                                } else {
                                                    setFilterValues((prev) => ({
                                                        ...prev,
                                                        category: { id: null, name: "" },
                                                        subcategory: { id: null, name: "" },
                                                    }));
                                                }
                                            }}
                                            onChangeSubcategory={(currentSubcategory, currentCategory) => {
                                                if (currentSubcategory.isPicked) {
                                                    setFilterValues((prev) => ({
                                                        ...prev,
                                                        category: {
                                                            id: currentCategory.id,
                                                            name: currentCategory.name,
                                                        },
                                                        subcategory: {
                                                            id: currentSubcategory.id,
                                                            name: currentSubcategory.name,
                                                        },
                                                    }));
                                                    setIsPickerOpen(false);
                                                } else {
                                                    setFilterValues((prev) => ({
                                                        ...prev,
                                                        category: { id: null, name: "" },
                                                        subcategory: { id: null, name: "" },
                                                    }));
                                                }
                                            }}
                                            serviceCategories={categoriesListForPicker}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </Col>
                    <Col xs="auto">
                        <Button
                            variant="outline-secondary"
                            className="d-flex align-items-center justify-content-center h-100"
                            style={{ width: 50 }}
                            disabled={isClearDisabled}
                            onClick={() => {
                                setSearchDraft("");
                                setFilterValues({
                                    searchValue: "",
                                    category: { id: null, name: "" },
                                    subcategory: { id: null, name: "" },
                                });
                            }}
                        >
                            <BrushIcon width={23} height={23} />
                        </Button>
                    </Col>
                </Row>

                {loading || !dishesReady ? (
                    <div className="d-flex justify-content-center py-4 mt-4">
                        <Spinner heightContent={true} />
                    </div>
                ) : (
                    <div className="mt-3">
                        <div className="d-flex flex-column" style={{ maxHeight: 420, overflowY: "auto" }}>
                            {isEmpty && (hasSearch ? <EmptyDishListBySearch /> : <EmptyDishList />)}

                            {!isEmpty &&
                                dishesListForPicker.map((dish) => {
                                    const picked = selectedIds.has(dish.id);
                                    const dishNameVm = buildTranslationTextVm({
                                        translatedValue: dish.name,
                                        originValue: dish.nameOrigin,
                                        isTranslationMode,
                                    });

                                    return (
                                        <div
                                            key={dish.id}
                                            className={`d-flex align-items-center justify-content-between px-3 py-2 mb-2 me-3 rounded-2 ${
                                                picked ? "bg-additional-violet" : ""
                                            }`}
                                        >
                                            <div
                                                className={`d-flex align-items-center gap-3 flex-grow-1`}
                                                onClick={() => toggleDish(dish)}
                                                role="button"
                                                style={{
                                                    cursor: `${dish.isAvailableForCurrentMenu ? "pointer" : "default"}`,
                                                }}
                                            >
                                                <div
                                                    className={`rounded-circle overflow-hidden flex-shrink-0 ${!dish.isAvailableForCurrentMenu ? "opacity-50" : ""}`}
                                                    style={{ width: 30, height: 30 }}
                                                >
                                                    <img
                                                        src={dish.favicon ? `${basePath}/${dish.favicon}` : altDishImg}
                                                        alt={dishNameVm.displayText}
                                                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                    />
                                                </div>

                                                <div
                                                    className={`d-flex align-items-center ${
                                                        dish.isAvailableForCurrentMenu
                                                            ? picked
                                                                ? "text-primary"
                                                                : ""
                                                            : "text-grey"
                                                    }`}
                                                >
                                                    <span className="me-1">
                                                        {cloneNameToDisplay(dishNameVm.displayText, translate)}
                                                    </span>

                                                    {dishNameVm.isMissingTranslation ? (
                                                        <TranslationMissingDot tooltipText={translationTooltip} />
                                                    ) : null}

                                                    {!dish.isAvailableForCurrentMenu && (
                                                        <OverlayTrigger
                                                            placement="bottom"
                                                            overlay={<Tooltip>{unavailableTooltip}</Tooltip>}
                                                        >
                                                            <span
                                                                className="bg-additional-grey rounded py-1 px-2 d-inline-flex align-items-center text-grey ms-2 fs-7"
                                                                style={{ cursor: "pointer" }}
                                                            >
                                                                <span className="me-1">{translate("Unavailable")}</span>
                                                                <i className="bi bi-info-circle"></i>
                                                            </span>
                                                        </OverlayTrigger>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="mx-2" style={{ width: 20 }}>
                                                {picked ? <i className="bi bi-check text-primary fw-bold" /> : null}
                                            </div>

                                            <WithRoleAccess permission={CompanyAccess.MenuDishEdit}>
                                                <Link
                                                    to={`/menu/update-dish/dish/${dish.id}/branch/${props.branchId}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => {
                                                        if (!canEditDish) e.preventDefault();
                                                        e.stopPropagation();
                                                    }}
                                                >
                                                    <i className="bi bi-eye text-primary" />
                                                </Link>
                                            </WithRoleAccess>
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer className="border-top-0 pt-0 d-flex justify-content-end">
                <Button variant="primary" className="px-5" disabled={isAddDisabled} onClick={handleAdd}>
                    {translate("Add")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default React.memo(AddDishesModal);
